/**
 * URL Validation Utility
 * Validates URL format including query parameters, projectname, and identificationID
 * Supports both generalized (?projectname=token) and personalized (?Project_ID=xxx&Token=yyy) formats
 */

// Valid weblink routes that require URL parameter validation
const VALID_WEBLINK_ROUTES = [
  '/dashboard-resp',
  '/sign-up',
  '/login-Resp',
  '/enter-otp'
];

/**
 * Validates if the route path is a valid weblink route
 * @param {string} pathname - The route path to validate
 * @returns {boolean} - Returns true if route is valid
 */
export const isValidWebLinkRoute = (pathname = window.location.pathname) => {
  return VALID_WEBLINK_ROUTES.includes(pathname);
};

/**
 * Validates URL format and extracts parameters
 * @param {string} url - The URL to validate
 * @param {string} pathname - The route pathname (optional, defaults to current pathname)
 * @returns {Object} - { isValid: boolean, error: string, projectname: string, identificationID: string, projectId: string, token: string }
 */
export const validateWebLinkUrl = (url = window.location.href, pathname = window.location.pathname) => {
  // First check if the route path is valid
  if (!isValidWebLinkRoute(pathname)) {
    return {
      isValid: false,
      error: `Invalid route: "${pathname}" is not a valid weblink route. Valid routes are: ${VALID_WEBLINK_ROUTES.join(', ')}`,
      projectname: null,
      identificationID: null,
      projectId: null,
      token: null
    };
  }
  // Check if URL contains query string separator
  if (!url.includes('?')) {
    return {
      isValid: false,
      error: 'Invalid URL format: Missing query parameter separator (?)',
      projectname: null,
      identificationID: null,
      projectId: null,
      token: null
    };
  }

  // Check if URL contains parameter value separator
  if (!url.includes('=')) {
    return {
      isValid: false,
      error: 'Invalid URL format: Missing parameter value separator (=)',
      projectname: null,
      identificationID: null,
      projectId: null,
      token: null
    };
  }

  try {
    const urlObj = new URL(url);
    const searchParams = new URLSearchParams(urlObj.search);
    
    // Check for personalized format (Project_ID and Token)
    const projectId = searchParams.get('Project_ID');
    const token = searchParams.get('Token');
    
    if (projectId && token) {
      // Personalized format: ?Project_ID=xxx&Token=yyy
      if (!projectId.trim() || !token.trim()) {
        return {
          isValid: false,
          error: 'Invalid URL format: Project_ID and Token cannot be empty',
          projectname: null,
          identificationID: null,
          projectId: null,
          token: null
        };
      }
      
      return {
        isValid: true,
        error: null,
        projectname: null,
        identificationID: token,
        projectId: projectId,
        token: token,
        isGeneralized: false
      };
    }
    
    // Check for generalized format (dynamic project name)
    // Get the first parameter key (project name)
    const firstParamKey = searchParams.keys().next().value;
    
    if (!firstParamKey) {
      return {
        isValid: false,
        error: 'Invalid URL format: No query parameters found',
        projectname: null,
        identificationID: null,
        projectId: null,
        token: null
      };
    }
    
    const identificationID = searchParams.get(firstParamKey);
    
    if (!identificationID || !identificationID.trim()) {
      return {
        isValid: false,
        error: 'Invalid URL format: Identification ID (token) is missing or empty',
        projectname: null,
        identificationID: null,
        projectId: null,
        token: null
      };
    }
    
    // Validate projectname format (should not be empty and should be a valid parameter name)
    if (!firstParamKey.trim()) {
      return {
        isValid: false,
        error: 'Invalid URL format: Project name cannot be empty',
        projectname: null,
        identificationID: null,
        projectId: null,
        token: null
      };
    }
    
    return {
      isValid: true,
      error: null,
      projectname: firstParamKey,
      identificationID: identificationID,
      projectId: null,
      token: identificationID,
      isGeneralized: true
    };
    
  } catch (error) {
    return {
      isValid: false,
      error: `Invalid URL format: ${error.message}`,
      projectname: null,
      identificationID: null,
      projectId: null,
      token: null
    };
  }
};

/**
 * Validates URL format on component mount
 * Redirects to error page or shows error if URL is invalid
 * @param {Function} navigate - React Router navigate function
 * @param {Function} setError - Function to set error state
 * @returns {boolean} - Returns true if URL is valid, false otherwise
 */
export const validateUrlOnMount = (navigate, setError) => {
  const validation = validateWebLinkUrl();
  
  if (!validation.isValid) {
    if (setError) {
      setError(validation.error);
    }
    // Optionally redirect to error page or landing page
    // navigate('/');
    return false;
  }
  
  return true;
};

/**
 * Extracts URL parameters safely
 * @param {string} url - The URL to parse (defaults to current URL)
 * @returns {Object} - Extracted parameters
 */
export const extractUrlParams = (url = window.location.href) => {
  const validation = validateWebLinkUrl(url);
  
  if (!validation.isValid) {
    return {
      projectname: null,
      identificationID: null,
      projectId: null,
      token: null,
      isGeneralized: null
    };
  }
  
  return {
    projectname: validation.projectname,
    identificationID: validation.identificationID,
    projectId: validation.projectId,
    token: validation.token,
    isGeneralized: validation.isGeneralized
  };
};

export default {
  validateWebLinkUrl,
  validateUrlOnMount,
  extractUrlParams,
  isValidWebLinkRoute
};

